import { useEffect, useRef } from 'react';

export default function StoryThreeBackground({ stageRef, ready }) {
  const hostRef = useRef(null);

  useEffect(() => {
    if (!ready) return undefined;
    const host = hostRef.current;
    const stage = stageRef.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 768px) and (hover: hover) and (pointer: fine)');
    let disposed = false;
    let visible = false;
    let pending = false;
    let failed = false;
    let frame = 0;
    let renderer;
    let geometry;
    let material;
    let scene;
    let camera;
    let points;
    let resizeObserver;
    let previousTime = 0;
    let elapsed = 0;
    const target = { x: 0, y: 0 };
    const positions = new Float32Array(28 * 3);
    const origins = new Float32Array(28 * 3);

    function resize() {
      if (!renderer) return;
      const width = host.clientWidth;
      const height = host.clientHeight;
      renderer.setSize(width, height, false);
      camera.aspect = width / Math.max(height, 1);
      camera.updateProjectionMatrix();
      geometry.setDrawRange(0, window.innerWidth < 768 ? 12 : 28);
    }

    function render(time) {
      frame = 0;
      if (disposed || !visible || document.hidden || reduced.matches || !renderer) return;
      elapsed += previousTime ? Math.min((time - previousTime) / 1000, 0.05) : 0;
      previousTime = time;
      for (let i = 0; i < 28; i += 1) {
        positions[i * 3] = origins[i * 3] + Math.sin(elapsed * 0.18 + i) * 0.035;
        positions[i * 3 + 1] = origins[i * 3 + 1] + Math.sin(elapsed * 0.12 + i * 0.7) * 0.06;
      }
      geometry.attributes.position.needsUpdate = true;
      points.rotation.x += (target.x - points.rotation.x) * 0.04;
      points.rotation.y += (target.y - points.rotation.y) * 0.04;
      renderer.render(scene, camera);
      frame = requestAnimationFrame(render);
    }

    async function wake() {
      if (disposed || reduced.matches || !visible || document.hidden || failed) return;
      if (!renderer && !pending) {
        pending = true;
        try {
          const THREE = await import('three');
          if (disposed || reduced.matches) return;
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('webgl2', { alpha: true, antialias: false, powerPreference: 'low-power' });
          if (!context) { failed = true; return; }
          renderer = new THREE.WebGLRenderer({ canvas, context, alpha: true, antialias: false });
          renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
          renderer.setClearColor(0x000000, 0);
          host.appendChild(canvas);
          scene = new THREE.Scene();
          camera = new THREE.PerspectiveCamera(40, 1, 0.1, 10);
          camera.position.z = 3;
          const colors = new Float32Array(28 * 3);
          for (let i = 0; i < 28; i += 1) {
            // Deterministic, sparse dust: green and gold with only two red points.
            origins[i * 3] = (Math.sin(i * 127.1 + 1) * 0.5 + 0.5) * 2.8 - 1.4;
            origins[i * 3 + 1] = (Math.sin(i * 311.7 + 2) * 0.5 + 0.5) * 2.1 - 1.05;
            origins[i * 3 + 2] = Math.sin(i * 73.3) * 0.2;
            new THREE.Color(i % 17 === 0 ? '#D84A36' : i % 3 === 0 ? '#F6CD36' : '#145844').toArray(colors, i * 3);
          }
          positions.set(origins);
          geometry = new THREE.BufferGeometry();
          geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
          geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          material = new THREE.PointsMaterial({ size: 1.5, sizeAttenuation: false, vertexColors: true, transparent: true, opacity: 0.22, depthWrite: false });
          points = new THREE.Points(geometry, material);
          scene.add(points);
          resizeObserver = new ResizeObserver(resize);
          resizeObserver.observe(host);
          resize();
        } catch {
          failed = true;
          resizeObserver?.disconnect();
          geometry?.dispose();
          material?.dispose();
          renderer?.dispose();
          renderer?.domElement.remove();
          renderer = undefined;
        } finally {
          pending = false;
        }
      }
      if (renderer && !frame && !disposed && visible && !reduced.matches && !document.hidden) {
        previousTime = 0;
        frame = requestAnimationFrame(render);
      }
    }

    function stop() { cancelAnimationFrame(frame); frame = 0; previousTime = 0; }
    function move(event) {
      if (!desktop.matches || reduced.matches) return;
      const rect = stage.getBoundingClientRect();
      target.y = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1)) * 0.04;
      target.x = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1)) * 0.025;
    }
    function leave() { target.x = 0; target.y = 0; }
    function motionChanged() { leave(); if (reduced.matches || document.hidden) stop(); else wake(); }
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) wake(); else stop();
    });
    observer.observe(stage);
    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerleave', leave);
    reduced.addEventListener('change', motionChanged);
    desktop.addEventListener('change', leave);
    document.addEventListener('visibilitychange', motionChanged);

    return () => {
      disposed = true;
      stop();
      observer.disconnect();
      resizeObserver?.disconnect();
      stage.removeEventListener('pointermove', move);
      stage.removeEventListener('pointerleave', leave);
      reduced.removeEventListener('change', motionChanged);
      desktop.removeEventListener('change', leave);
      document.removeEventListener('visibilitychange', motionChanged);
      geometry?.dispose();
      material?.dispose();
      renderer?.dispose();
      renderer?.domElement.remove();
    };
  }, [ready, stageRef]);

  return <div ref={hostRef} className="our-story__particles" aria-hidden="true" />;
}
